import {
  SalaryTrendsResult,
  SalaryTrendType,
} from '@/lib/generated/graphql-types'

import {
  BASE_SPECIALTY_GROUP_ID,
  FACTOR_FOR_MAX_TOTAL_COMPENSATION,
  OTHER_GROUP_ID,
  SalaryTrendCategory,
  SalaryTrendGroupId,
  TrendCategory,
  trendExtraInfoByGroupType,
  TrendGroup,
  trendLabelByGroupLabel,
} from './constants'
import { MainRoutes } from '@/utils/routes'
import {
  JOB_TYPE_PATH_INDEX,
  PROFESSION_OR_OCCUPATION_PATH_INDEX,
  SALARY_PATH,
  SPLIT_SALARY_INDEX_OFFSET,
  STATE_PATH_INDEX,
} from '../filters/get-salary-filters-from-url'
import { SALARY_FULL_TIME_VALUE } from '@/modules/surveys/configs/onboarding/current-job-pane-config'
import {
  professsionSlugIds,
  NURSE_MIDWIFE_SLUG,
  ANESTHESIOLOGIST_ASSISTANT_SLUG,
  NURSE_ANESTHETIST_SLUG,
  PHYSICIAN_SLUG,
} from '@/modules/surveys/configs/onboarding/personal-info-config'
import { DropdownWithSlugId } from '../types'

// Non-searchable professions that should be treated as occupations
const NON_SEARCHABLE_PROFESSIONS = [
  NURSE_MIDWIFE_SLUG,
  ANESTHESIOLOGIST_ASSISTANT_SLUG,
  NURSE_ANESTHETIST_SLUG,
]

export function isSearchableProfession(slug: string | null): boolean {
  if (!slug) {
    return false
  }
  return (
    Object.values(professsionSlugIds).includes(slug) &&
    !NON_SEARCHABLE_PROFESSIONS.includes(slug)
  )
}

export function sortSpecaltyGroup(
  trends: SalaryTrendType[] | null | undefined,
  occupation: string | null,
): SalaryTrendType[] {
  if (!trends) {
    return []
  }

  const isPhysicianSearch = occupation === PHYSICIAN_SLUG

  return [...trends].sort((a, b) => {
    // If either trend has groupLabel "other", move it to the end
    if (a.groupLabel === OTHER_GROUP_ID && b.groupLabel !== OTHER_GROUP_ID) {
      return 1
    }
    if (a.groupLabel !== OTHER_GROUP_ID && b.groupLabel === OTHER_GROUP_ID) {
      return -1
    }
    if (a.groupLabel === OTHER_GROUP_ID && b.groupLabel === OTHER_GROUP_ID) {
      return 0
    }

    // Sort by groupType (base_specialty_id first)
    if (
      a.groupType === BASE_SPECIALTY_GROUP_ID &&
      b.groupType !== BASE_SPECIALTY_GROUP_ID
    ) {
      return -1
    }
    if (
      a.groupType !== BASE_SPECIALTY_GROUP_ID &&
      b.groupType === BASE_SPECIALTY_GROUP_ID
    ) {
      return 1
    }
    // If same groupType, sort alphabetically by label
    const labelA = getTrendSortLabel(a, isPhysicianSearch).toLowerCase()
    const labelB = getTrendSortLabel(b, isPhysicianSearch).toLowerCase()
    return labelA.localeCompare(labelB)
  })
}

export function transformSalaryTrends(
  data: SalaryTrendsResult | null | undefined,
  occupation: string | null,
): TrendCategory[] {
  if (!data) {
    return []
  }

  const isProfessionSearch = isSearchableProfession(occupation)
  const categories: TrendCategory[] = [
    {
      id: SalaryTrendCategory.EmployerBreakdown,
      title: 'Employer Breakdown',
      enabled: true,
      groups: [
        {
          id: SalaryTrendGroupId.AcademicAffiliation,
          title: 'Academic Affiliation',
          trends: data.academicAffiliationTrends || [],
          enabled: (data.academicAffiliationTrends || []).length > 0,
        },
        {
          id: SalaryTrendGroupId.EmployerType,
          title: 'Employer Type',
          trends: data.employerTypeTrends || [],
          enabled: (data.employerTypeTrends || []).length > 0,
        },
        {
          id: SalaryTrendGroupId.CompensationModel,
          title: 'Compensation Model',
          trends: data.compModelTrends || [],
          enabled: (data.compModelTrends || []).length > 0,
        },
        {
          id: SalaryTrendGroupId.TaxStatus,
          title: 'Tax Status',
          trends: data.taxStatusTrends || [],
          enabled: (data.taxStatusTrends || []).length > 0,
        },
      ],
    },
    {
      id: SalaryTrendCategory.SpecializationAndDemographic,
      title: getSpecialtyDemographicsCategoryLabel(data),
      enabled: true,
      groups: [
        {
          id: SalaryTrendGroupId.Specialty,
          title: isProfessionSearch ? 'Specialty' : 'Sub-Specialty',
          trends: isProfessionSearch
            ? sortSpecaltyGroup(data.specialtyTrends, occupation)
            : data.specialtyTrends,
          enabled: (data.specialtyTrends || []).length > 0,
        },
        {
          id: SalaryTrendGroupId.Experience,
          title: 'Experience',
          trends: data.yoeTrends || [],
          enabled: (data.yoeTrends || []).length > 0,
        },
      ],
    },
    {
      id: SalaryTrendCategory.GeographicBreakdown,
      title: 'Location Breakdown',
      enabled: true,
      groups: [
        {
          id: SalaryTrendGroupId.Region,
          title: 'Region',
          trends: data.countryRegionTrends || [],
          enabled: (data.countryRegionTrends || []).length > 0,
        },
        {
          id: SalaryTrendGroupId.MetroType,
          title: 'Town Size',
          trends: data.metroTypeTrends || [],
          enabled: (data.metroTypeTrends || []).length > 0,
        },
        {
          id: SalaryTrendGroupId.CostOfLiving,
          title: 'Cost of Living',
          trends: data.colTrends || [],
          enabled: (data.colTrends || []).length > 0,
        },
      ],
    },
  ]

  // Set category enabled based on whether any of its groups are enabled
  return categories.map((category) => ({
    ...category,
    enabled: category.groups.some((group) => group.enabled),
  }))
}

export function getMaxTotalCompensation(categories: TrendCategory[]): number {
  let maxTotal = 0

  for (const category of categories) {
    for (const group of category.groups) {
      for (const trend of group.trends) {
        const total =
          (trend.avgSalary || 0) +
          (trend.avgBonuses || 0) +
          (trend.avgOtherIncome || 0)
        maxTotal = Math.max(maxTotal, total)
      }
    }
  }

  // to the max value add FACTOR_FOR_MAX_TOTAL_COMPENSATION to display
  // more data in the chart
  return maxTotal * FACTOR_FOR_MAX_TOTAL_COMPENSATION
}

export function getRatingColor(rating: number | undefined | null): string {
  const roundedRating = rating ? Math.round(rating * 10) / 10 : 0

  if (roundedRating >= 3.5) {
    return 'green'
  }
  if (roundedRating >= 2) {
    return 'yellow'
  }
  if (roundedRating > 0) {
    return 'red'
  }
  return 'black'
}

export function getTrendLabelObject(
  trend: SalaryTrendType,
  occupation: string | null,
) {
  const { groupLabel, occupation: trendOccupation } = trend
  const isPhysicianSearch = occupation === PHYSICIAN_SLUG
  const isProfessionSearch = isSearchableProfession(occupation)

  if (trendOccupation) {
    const url = [
      MainRoutes.Salary,
      SALARY_FULL_TIME_VALUE,
      trendOccupation.slug,
      SALARY_PATH,
    ]
      .filter(Boolean)
      .join('/')

    if (isPhysicianSearch) {
      return {
        label: trendOccupation.specialty.fullName,
        url,
      }
    } else if (isProfessionSearch) {
      return {
        label: trendOccupation.fullTitle,
        url,
      }
    } else {
      const isBaseSpecialty = trend.groupType === BASE_SPECIALTY_GROUP_ID
      return {
        label: isBaseSpecialty
          ? `${trendOccupation.specialty.fullName} - All`
          : trendOccupation.fullTitle,
        url,
      }
    }
  }
  return {
    label:
      trendLabelByGroupLabel[
        groupLabel as keyof typeof trendLabelByGroupLabel
      ] ?? groupLabel,
    url: null,
  }
}

export function getTrendSortLabel(
  trend: SalaryTrendType,
  isPhysicianSearch: boolean,
): string {
  const { groupLabel, occupation } = trend
  if (occupation) {
    return isPhysicianSearch
      ? occupation.specialty.fullName
      : occupation.baseTitlePlusSpecialty
  }
  return groupLabel
}

const isTrendsEnabled =
  process.env.NEXT_PUBLIC_FEATURE_FLAG_SALARY_TRENDS_ENABLED === 'true'

export function hasTrendsTab(
  jobType: string | null | undefined,
  state: string | DropdownWithSlugId | null | undefined,
) {
  return isTrendsEnabled && !state && jobType === SALARY_FULL_TIME_VALUE
}

export function getSpecialtyDemographicsCategoryLabel(
  data: SalaryTrendsResult,
) {
  const hasSpecialtyTrends = data.specialtyTrends.length > 0
  const hasExperienceTrends = data.yoeTrends.length > 0
  const hasGenderTrends = data.genderTrends.length > 0

  if (
    (hasSpecialtyTrends && (hasExperienceTrends || hasGenderTrends)) ||
    (!hasSpecialtyTrends && !hasExperienceTrends && !hasGenderTrends)
  ) {
    return 'Specialty & Demographics'
  }

  if (hasSpecialtyTrends && !hasExperienceTrends && !hasGenderTrends) {
    return 'Specialty'
  }

  return 'Demographics'
}

export function getExtraInfoByGroupId(group: TrendGroup | null | undefined) {
  if (!group) {
    return null
  }

  const { id: groupId, trends } = group

  const extraInfo =
    trendExtraInfoByGroupType[groupId as keyof typeof trendExtraInfoByGroupType]

  if (!extraInfo) {
    return null
  }

  return extraInfo
    .filter((info) => {
      const trend = trends.find((t) => t.groupLabel === info.enabled)
      return trend
    })
    .map((info) => info.label)
    .join(', ')
}

/**
 * From the pathname, checks if trends url is valid
 * and returns the pathname if it is
 * or salary pathname if it is not (keeping the same filters)
 * @param pathname
 * @returns
 */
export function getTrendsPathname(pathname: string) {
  const pathValues = pathname.split('/')

  if (pathValues.length <= STATE_PATH_INDEX + SPLIT_SALARY_INDEX_OFFSET) {
    return pathname // Return early if path is too short
  }

  const locationOrTrends =
    pathValues[STATE_PATH_INDEX + SPLIT_SALARY_INDEX_OFFSET]
  const jobType = pathValues[JOB_TYPE_PATH_INDEX + SPLIT_SALARY_INDEX_OFFSET]
  const occupation =
    pathValues[PROFESSION_OR_OCCUPATION_PATH_INDEX + SPLIT_SALARY_INDEX_OFFSET]

  if (
    isTrendsEnabled &&
    locationOrTrends === 'trends' &&
    jobType === SALARY_FULL_TIME_VALUE &&
    occupation
  ) {
    return pathname
  }

  return pathname.replace('/trends', '')
}
